// ─── Shared styles — earthy orange palette (matches cycloidal_drives style) ──
const S = {
  wrapper:      { marginTop:'10px', padding:'40px', border:'3px solid #7a2e00', fontFamily:"'Georgia',serif", color:'#1a0e06', lineHeight:'1.7', background:'#fdf6ee' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c45a10', paddingBottom:'8px', marginBottom:'24px', color:'#7a2e00' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#a04010', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c45a10', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#fdf0e4', border:'2px solid #c45a10', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New',monospace", fontSize:'0.88rem', color:'#7a2e00', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c45a10' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#a04010', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #e8c89a', verticalAlign:'top', background:'#fdf4ec' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #e8c89a', verticalAlign:'top', background:'#fdf6ee' },
  tdL:          { fontWeight:'600', color:'#7a2e00', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New',monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#fdf4ec', border:'1px solid #e8b07a', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#7a2e00', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#7a4a28', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((it,i)=><div key={i} style={S.infoBoxItem}>{it}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{...S.th,width:'220px'}}>Concept</th>
        <th style={S.th}>Detail</th>
      </tr></thead>
      <tbody>{rows.map(([l,v],i)=>(
        <tr key={i}>
          <td style={{...(i%2===0?S.tdE:S.tdO),...S.tdL}}>{l}</td>
          <td style={{...(i%2===0?S.tdE:S.tdO),...S.code}}>{v}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 1 — What is surface roughness? Cross-section anatomy
// Shows a magnified surface profile with Ra, Rz, peaks, valleys labelled
// ─────────────────────────────────────────────────────────────────────
function DiagramProfileAnatomy() {
  const W = 960, H = 370;

  // Generate a realistic-looking rough surface profile
  // Mean line at y=200, peaks and valleys around it
  const profilePoints = [
    [30,200],[60,185],[80,170],[100,182],[120,165],[145,172],[165,155],[185,175],
    [205,168],[225,185],[240,178],[260,192],[278,170],[295,183],[310,162],[330,175],
    [348,188],[365,170],[380,180],[400,160],[415,178],[430,185],[450,168],[465,192],
    [480,172],[498,183],[512,162],[528,180],[545,175],[562,185],[578,168],[595,190],
    [610,173],[628,180],[645,165],[660,182],[675,172],[690,188],[705,170],[720,183],
    [735,162],[750,175],[768,188],[785,170],[800,182],[815,167],[830,180],[845,192],
    [860,173],[875,183],[890,165],[910,180],[930,200],
  ];

  const meanY = 200;
  const profileStr = profilePoints.map(([x,y])=>`${x},${y}`).join(' ');

  // Find highest peak and deepest valley for Rz illustration
  const peakPt   = [310, 162]; // highest peak (lowest y)
  const valleyPt = [465, 192]; // deepest valley (highest y)

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Surface Profile Anatomy — What Is Surface Roughness?</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="arSR" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
          <marker id="arSRl" markerWidth="7" markerHeight="7" refX="2" refY="3.5" orient="auto">
            <polygon points="6 0,0 3.5,6 7" fill="#c45a10"/>
          </marker>
          <marker id="arBlue" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#2a6496"/>
          </marker>
          <marker id="arBluel" markerWidth="7" markerHeight="7" refX="2" refY="3.5" orient="auto">
            <polygon points="6 0,0 3.5,6 7" fill="#2a6496"/>
          </marker>
          {/* Hatch fill for material below profile */}
          <pattern id="matHatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="6" stroke="#d4874a" strokeWidth="1.5" opacity="0.5"/>
          </pattern>
        </defs>

        {/* Material fill below profile */}
        <polygon
          points={`30,${H-20} ${profileStr} 930,${H-20}`}
          fill="url(#matHatch)" stroke="none"
        />

        {/* Surface profile line */}
        <polyline points={profileStr} fill="none" stroke="#7a2e00" strokeWidth="2.5"/>

        {/* Mean line (Ra reference) */}
        <line x1={30} y1={meanY} x2={930} y2={meanY}
          stroke="#2a6496" strokeWidth="1.8" strokeDasharray="8,4"/>
        <text x={935} y={meanY+4} fontSize="10" fontWeight="700" fill="#2a6496">Mean line</text>

        {/* Ra shading — vertical bars above and below mean line at a few points */}
        {[[80,170],[145,172],[165,155],[310,162],[400,160],[512,162],[645,165],[735,162],[890,165]].map(([x,y],i)=>(
          <rect key={`r${i}`} x={x-3} y={Math.min(y,meanY)} width={6}
            height={Math.abs(meanY-y)} fill="#c45a10" opacity="0.28"/>
        ))}
        {[[60,185],[100,182],[225,185],[260,192],[430,185],[595,190],[690,188],[845,192]].map(([x,y],i)=>(
          <rect key={`v${i}`} x={x-3} y={meanY} width={6}
            height={Math.abs(meanY-y)} fill="#c45a10" opacity="0.28"/>
        ))}

        {/* Ra label */}
        <line x1={145} y1={156} x2={145} y2={200} stroke="#c45a10" strokeWidth="1.5"
          strokeDasharray="3,2" markerEnd="url(#arSR)" markerStart="url(#arSRl)"/>
        <rect x={60} y={118} width={132} height={32} rx={4} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={126} y={133} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Ra — Arithmetic Mean</text>
        <text x={126} y={145} textAnchor="middle" fontSize="8.5" fill="#a04010">avg. of |deviations from mean|</text>
        <line x1={126} y1={150} x2={145} y2={156} stroke="#c45a10" strokeWidth="1.2"/>

        {/* Rz double-arrow: from highest peak to deepest valley in a sampling length */}
        <line x1={peakPt[0]} y1={peakPt[1]} x2={valleyPt[0]} y2={valleyPt[1]}
          stroke="#2a6496" strokeWidth="2" strokeDasharray="4,3"
          markerEnd="url(#arBlue)" markerStart="url(#arBluel)"/>
        <rect x={350} y={116} width={170} height={32} rx={4} fill="#fdf0e4" stroke="#2a6496" strokeWidth="1.5"/>
        <text x={435} y={131} textAnchor="middle" fontSize="10" fontWeight="700" fill="#2a6496">Rz — 10-point Height</text>
        <text x={435} y={143} textAnchor="middle" fontSize="8.5" fill="#2a6496">highest peak to lowest valley</text>
        <line x1={390} y1={148} x2={355} y2={168} stroke="#2a6496" strokeWidth="1.2"/>

        {/* Sampling length bracket */}
        <line x1={280} y1={H-30} x2={540} y2={H-30} stroke="#7a2e00" strokeWidth="1.5"
          markerEnd="url(#arSR)" markerStart="url(#arSRl)"/>
        <text x={410} y={H-15} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">
          Sampling length (lr) — typically 0.8 mm for Ra 0.1–2 µm
        </text>

        {/* Evaluation length bracket */}
        <line x1={30} y1={H-50} x2={930} y2={H-50} stroke="#a04010" strokeWidth="1.2"
          strokeDasharray="5,3" markerEnd="url(#arSR)" markerStart="url(#arSRl)"/>
        <text x={480} y={H-55} textAnchor="middle" fontSize="8.5" fill="#a04010">
          Evaluation length (ln) = 5 × sampling length
        </text>

        {/* Peak callout */}
        <line x1={310} y1={162} x2={310} y2={135} stroke="#7a2e00" strokeWidth="1.2"/>
        <circle cx={310} cy={162} r={4} fill="#c45a10"/>
        <text x={310} y={128} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">Peak (Rp)</text>

        {/* Valley callout */}
        <line x1={465} y1={192} x2={510} y2={220} stroke="#7a2e00" strokeWidth="1.2"/>
        <circle cx={465} cy={192} r={4} fill="#c45a10"/>
        <text x={535} y={228} fontSize="9" fontWeight="700" fill="#7a2e00">Valley (Rv)</text>

        {/* Right facts panel */}
        <rect x={660} y={10} width={290} height={260} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={805} y={27} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Key Roughness Parameters</text>
        <line x1={668} y1={33} x2={942} y2={33} stroke="#e8b07a"/>
        {[
          ['Ra','Arithmetic mean deviation (µm)'],
          ['','Most common spec — ISO 4287'],
          ['Rq (RMS)','Root mean square roughness'],
          ['','Rq ≈ 1.11 × Ra typically'],
          ['Rz','Avg of 5 peak-to-valley heights'],
          ['','within evaluation length'],
          ['Rt','Max peak-to-valley overall'],
          ['','= single worst-case height'],
          ['Rp / Rv','Max peak / max valley height'],
          ['','from mean line'],
          ['Rsm','Mean spacing of profile peaks'],
          ['','(wavelength of texture)'],
          ['Rsk','Skewness — asymmetry of profile'],
          ['','< 0: plateau; > 0: sharp peaks'],
          ['Rku','Kurtosis — sharpness of profile'],
          ['','> 3: sharp; < 3: bumpy'],
          ['Rl / Rlo','Developed profile length ratio'],
          ['','longer = more surface area'],
        ].map(([l,v],i)=>(
          <g key={i}>
            <text x={668} y={46+i*12} fontSize="9" fontWeight={l?'700':'400'} fill={l?'#7a2e00':'#7a4a28'}>{l}</text>
            <text x={l?708:668} y={46+i*12} fontSize="9" fill="#a04010">{v}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        A real surface is never perfectly flat — every manufacturing process leaves characteristic peaks and valleys. Parameters like Ra quantify these deviations from a mean reference line.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Manufacturing process roughness chart (horizontal bar chart)
// Shows typical Ra ranges for major manufacturing processes
// ─────────────────────────────────────────────────────────────────────
function DiagramProcessRanges() {
  // Extra height: zone band row (30px) + legend row (20px) + margins
  const W = 960, H = 500;

  const processes = [
    ['Super-finishing',      0.006, 0.05,  'Abrasive'],
    ['Lapping',              0.01,  0.4,   'Abrasive'],
    ['Honing',               0.05,  0.8,   'Abrasive'],
    ['Cylindrical grinding', 0.1,   1.6,   'Abrasive'],
    ['Surface grinding',     0.1,   3.2,   'Abrasive'],
    ['Diamond turning',      0.05,  1.6,   'Cutting'],
    ['Fine turning',         0.4,   3.2,   'Cutting'],
    ['Turning (general)',    0.8,   12.5,  'Cutting'],
    ['Milling (end mill)',   0.8,   6.3,   'Cutting'],
    ['Reaming',              0.4,   3.2,   'Cutting'],
    ['Drilling',             1.6,   12.5,  'Cutting'],
    ['Broaching',            0.4,   3.2,   'Cutting'],
    ['Die casting',          0.8,   6.3,   'Forming'],
    ['Investment casting',   1.6,   12.5,  'Forming'],
    ['Sand casting',         6.3,   50.0,  'Forming'],
    ['EDM (fine)',           0.2,   1.6,   'Other'],
    ['EDM (rough)',          1.6,   12.5,  'Other'],
    ['Laser cutting',        0.8,   12.5,  'Other'],
  ];

  const logMin = Math.log10(0.005), logMax = Math.log10(60);
  const chartX = 200, chartW = 720;

  // Layout: top label=12, axis ticks=30, zone band=60, bars start at barTop
  const zoneY = 36;   // top of zone band
  const zoneH = 28;   // height of zone band
  const barTop = zoneY + zoneH + 8; // bars start here
  const nProc = processes.length;
  const rowH = 20;    // fixed row height — comfortable spacing
  const barsH = nProc * rowH;
  const legendY = barTop + barsH + 14;

  const catColor = { Abrasive:'#c45a10', Cutting:'#7a2e00', Forming:'#d4874a', Other:'#a04010' };

  function xPos(ra) {
    return chartX + ((Math.log10(ra) - logMin) / (logMax - logMin)) * chartW;
  }

  const ticks = [0.006, 0.01, 0.025, 0.05, 0.1, 0.25, 0.5, 1, 2.5, 5, 10, 25, 50];
  // clamp Ra value to chart bounds for xPos
  function xClamped(ra) {
    const clamped = Math.max(0.005, Math.min(58, ra));
    return xPos(clamped);
  }

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Typical Ra Surface Roughness Ranges by Manufacturing Process</div>
      <svg viewBox={`0 0 ${W} ${legendY + 30}`} style={{width:'100%',display:'block'}}>

        {/* Axis label */}
        <text x={chartX + chartW/2} y={12} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">
          Ra (µm) — logarithmic scale
        </text>

        {/* Application zone band — sits between axis label and bars */}
        {[
          [0.006, 0.1,  'Mirror / optics',       '#c45a10'],
          [0.1,   1.6,  'Precision fits',          '#d4874a'],
          [1.6,   6.3,  'General engineering',     '#e8b07a'],
          [6.3,   55,   'Rough / structural',      '#f5d9b8'],
        ].map(([a, b, label, col], i) => {
          const x1 = xClamped(a), x2 = xClamped(b);
          return (
            <g key={i}>
              <rect x={x1} y={zoneY} width={x2 - x1} height={zoneH} fill={col} opacity={0.28} rx={2}/>
              <text x={(x1 + x2) / 2} y={zoneY + zoneH / 2 + 4} textAnchor="middle"
                fontSize="8" fontWeight="700" fill="#7a2e00">{label}</text>
            </g>
          );
        })}

        {/* Tick lines — run from just below axis label all the way through zone and bars */}
        {ticks.map((v, i) => {
          const x = xPos(v);
          return (
            <g key={i}>
              <line x1={x} y1={18} x2={x} y2={barTop + barsH} stroke="#e8c89a" strokeWidth={0.8} strokeDasharray="3,3"/>
              <text x={x} y={30} textAnchor="middle" fontSize="7.5" fill="#7a4a28">{v}</text>
            </g>
          );
        })}

        {/* Process bars */}
        {processes.map(([name, rMin, rMax, cat], i) => {
          const y = barTop + i * rowH;
          const bH = rowH * 0.72;
          const x1 = xClamped(rMin), x2 = xClamped(rMax);
          const col = catColor[cat];
          // clamp label x so it doesn't overflow right edge
          const labelX = Math.min(x2 + 4, W - 62);
          return (
            <g key={i}>
              <text x={chartX - 6} y={y + bH / 2 + 3.5} textAnchor="end" fontSize="8.5" fill="#7a2e00">{name}</text>
              <rect x={x1} y={y + 2} width={x2 - x1} height={bH} fill={col} rx={2} opacity="0.78"/>
              <text x={labelX} y={y + bH / 2 + 5.5} fontSize="7.5" fontFamily="'Courier New',monospace" fill="#7a2e00">
                {rMin}–{rMax}
              </text>
            </g>
          );
        })}

        {/* Legend */}
        {Object.entries(catColor).map(([cat, col], i) => (
          <g key={i}>
            <rect x={chartX + i * 150} y={legendY} width={14} height={10} fill={col} rx={2} opacity="0.78"/>
            <text x={chartX + i * 150 + 18} y={legendY + 9} fontSize="8.5" fill="#7a2e00">{cat}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Each bar shows the achievable Ra range for that process. Finer processes (left) cost more time and tooling but enable precision fits, sealing surfaces, and optics.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 3 — ISO surface texture symbol explained
// Shows the standard drawing symbol with all callout fields annotated
// ─────────────────────────────────────────────────────────────────────
function DiagramISOSymbol() {
  // Symbol: tip bx=310, by=340, legLen=90, angle=60deg
  // lx=265, ly=262, rx2=355, barEnd=500
  // Boxes top row:  a(x=80,w=210,cx=185)  b(x=360,w=240,cx=480)
  // Boxes bot row:  c(x=30,w=195,cx=127)  d(x=240,w=225,cx=352)  e(x=480,w=175,cx=567)
  // Leader lines go STRAIGHT from each box toward its own text on the symbol — no crossing.
  //   a-leader:  (185,150) → (295, ly-8)   [left of "Ra 0.8", above bar]
  //   b-leader:  (480,150) → (420, ly-18)  [right, above bar — stays right of centre]
  //   c-leader:  (127,420) → (280, ly+30)  [far-left box → left below-bar text]
  //   d-leader:  (352,420) → (300, ly+48)  [centre box → centre below-bar text]
  //   e-leader:  (567,420) → (450, ly+30)  [right box → right below-bar text]
  const W = 960, H = 490;
  const ly = 262, rx2 = 355;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>ISO 1302 Surface Texture Symbol — Reading Engineering Drawings</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",display:"block"}}>
        <defs>
          <marker id="arIS" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
        </defs>

        {/* Symbol body: tip(310,340) legs to (265,262) and (355,262), bar to x=500 */}
        <line x1={265} y1={262} x2={310} y2={340} stroke="#7a2e00" strokeWidth="4"/>
        <line x1={310} y1={340} x2={355} y2={262} stroke="#7a2e00" strokeWidth="4"/>
        <line x1={265} y1={262} x2={500} y2={262} stroke="#7a2e00" strokeWidth="4"/>
        <line x1={265} y1={275} x2={500} y2={275} stroke="#7a2e00" strokeWidth="1.8"/>

        {/* Values on symbol */}
        <text x={270} y={254} fontSize="16" fontWeight="700" fontFamily="'Courier New',monospace" fill="#c45a10">Ra 0.8</text>
        <text x={390} y={242} fontSize="13" fontFamily="'Courier New',monospace" fill="#a04010">Ra 1.6</text>
        <text x={270} y={292} fontSize="12" fontFamily="'Courier New',monospace" fill="#7a2e00">0.8-2.5</text>
        <text x={270} y={310} fontSize="12" fontFamily="'Courier New',monospace" fill="#7a2e00">= (lay)</text>
        <text x={445} y={292} fontSize="12" fontFamily="'Courier New',monospace" fill="#7a2e00">0.3</text>
        <line x1={310} y1={340} x2={282} y2={372} stroke="#7a2e00" strokeWidth="1.2"/>
        <text x={278} y={385} textAnchor="end" fontSize="9" fontWeight="700" fill="#7a2e00">Applied to this surface</text>

        {/* BOX a — top-left: x=80 w=210 cx=185 */}
        <rect x={80}  y={110} width={210} height={40} rx={4} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={185} y={126} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">a — Primary roughness value</text>
        <text x={185} y={140} textAnchor="middle" fontSize="8.5" fill="#a04010">Ra (or Rz etc.) + number, e.g. Ra 0.8 µm</text>
        <line x1={185} y1={150} x2={295} y2={254} stroke="#c45a10" strokeWidth="1.3"/>

        {/* BOX b — top-right: x=360 w=240 cx=480 */}
        <rect x={360} y={110} width={240} height={40} rx={4} fill="#fdf0e4" stroke="#a04010" strokeWidth="1.5"/>
        <text x={480} y={126} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">b — Second roughness requirement</text>
        <text x={480} y={140} textAnchor="middle" fontSize="8.5" fill="#a04010">Upper limit — same or different parameter</text>
        <line x1={480} y1={150} x2={420} y2={244} stroke="#a04010" strokeWidth="1.3"/>

        {/* BOX c — bottom-left: x=30 w=195 cx=127 */}
        <rect x={30}  y={420} width={195} height={40} rx={4} fill="#fdf0e4" stroke="#7a2e00" strokeWidth="1.5"/>
        <text x={127} y={436} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">c — Sampling / eval. length</text>
        <text x={127} y={450} textAnchor="middle" fontSize="8.5" fill="#a04010">e.g. 0.8 or 2.5 mm (ISO 4288)</text>
        <line x1={127} y1={420} x2={280} y2={292} stroke="#7a2e00" strokeWidth="1.3"/>

        {/* BOX d — bottom-centre: x=240 w=225 cx=352 */}
        <rect x={240} y={420} width={225} height={40} rx={4} fill="#fdf0e4" stroke="#7a2e00" strokeWidth="1.5"/>
        <text x={352} y={436} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">d — Lay direction symbol</text>
        <text x={352} y={450} textAnchor="middle" fontSize="8.5" fill="#a04010">= parallel · ⊥ perp · × crossed · C circ · R radial</text>
        <line x1={352} y1={420} x2={300} y2={310} stroke="#7a2e00" strokeWidth="1.3"/>

        {/* BOX e — bottom-right: x=480 w=175 cx=567 */}
        <rect x={480} y={420} width={175} height={40} rx={4} fill="#fdf0e4" stroke="#7a2e00" strokeWidth="1.5"/>
        <text x={567} y={436} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">e — Machining allowance</text>
        <text x={567} y={450} textAnchor="middle" fontSize="8.5" fill="#a04010">Stock to remove in mm (e.g. 0.3)</text>
        <line x1={567} y1={420} x2={450} y2={292} stroke="#7a2e00" strokeWidth="1.3"/>

        {/* Right panel */}
        <rect x={660} y={10} width={292} height={H-20} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={806} y={30} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Symbol Variants</text>
        <line x1={668} y1={37} x2={944} y2={37} stroke="#e8b07a"/>
        {(()=>{ const sx=700,sy=95,ll=34,a=Math.PI/3; return (<g>
          <line x1={sx-ll*Math.cos(a)} y1={sy-ll*Math.sin(a)} x2={sx} y2={sy} stroke="#7a2e00" strokeWidth="2.5"/>
          <line x1={sx} y1={sy} x2={sx+ll*Math.cos(a)} y2={sy-ll*Math.sin(a)} stroke="#7a2e00" strokeWidth="2.5"/>
          <text x={sx+44} y={sy-13} fontSize="9" fontWeight="700" fill="#7a2e00">Basic symbol</text>
          <text x={sx+44} y={sy+1}  fontSize="8.5" fill="#a04010">any process allowed</text>
        </g>); })()}
        {(()=>{ const sx=700,sy=168,ll=34,a=Math.PI/3,lx2=700-ll*Math.cos(a),ly2=168-ll*Math.sin(a),rx3=700+ll*Math.cos(a); return (<g>
          <line x1={lx2} y1={ly2} x2={sx}  y2={sy}  stroke="#7a2e00" strokeWidth="2.5"/>
          <line x1={sx}  y1={sy}  x2={rx3} y2={ly2} stroke="#7a2e00" strokeWidth="2.5"/>
          <circle cx={(lx2+rx3)/2} cy={ly2} r={8} fill="none" stroke="#7a2e00" strokeWidth="2"/>
          <text x={sx+44} y={sy-13} fontSize="9" fontWeight="700" fill="#7a2e00">Circle at junction</text>
          <text x={sx+44} y={sy+1}  fontSize="8.5" fill="#a04010">no material removal</text>
        </g>); })()}
        {(()=>{ const sx=700,sy=242,ll=34,a=Math.PI/3,lx2=700-ll*Math.cos(a),ly2=242-ll*Math.sin(a),rx3=700+ll*Math.cos(a); return (<g>
          <line x1={lx2} y1={ly2} x2={sx}      y2={sy}  stroke="#7a2e00" strokeWidth="2.5"/>
          <line x1={sx}  y1={sy}  x2={rx3}     y2={ly2} stroke="#7a2e00" strokeWidth="2.5"/>
          <line x1={lx2} y1={ly2} x2={rx3+36}  y2={ly2} stroke="#7a2e00" strokeWidth="2.5"/>
          <text x={sx+44} y={sy-13} fontSize="9" fontWeight="700" fill="#7a2e00">With horizontal bar</text>
          <text x={sx+44} y={sy+1}  fontSize="8.5" fill="#a04010">machining required</text>
        </g>); })()}
        {(()=>{ const sx=700,sy=316,ll=34,a=Math.PI/3,lx2=700-ll*Math.cos(a),ly2=316-ll*Math.sin(a),rx3=700+ll*Math.cos(a); return (<g>
          <line x1={lx2} y1={ly2} x2={sx}     y2={sy}  stroke="#7a2e00" strokeWidth="2.5"/>
          <line x1={sx}  y1={sy}  x2={rx3}    y2={ly2} stroke="#7a2e00" strokeWidth="2.5"/>
          <line x1={lx2} y1={ly2} x2={rx3+36} y2={ly2} stroke="#7a2e00" strokeWidth="2.5"/>
          <circle cx={lx2} cy={ly2} r={7} fill="none" stroke="#c45a10" strokeWidth="2"/>
          <text x={sx+44} y={sy-13} fontSize="9" fontWeight="700" fill="#7a2e00">Circle at leg (ISO)</text>
          <text x={sx+44} y={sy+1}  fontSize="8.5" fill="#a04010">applies to ALL surfaces</text>
        </g>); })()}
        <rect x={668} y={368} width={268} height={66} rx={4} fill="#fdf6ee" stroke="#e8b07a" strokeWidth="1.2"/>
        <text x={802} y={386} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">Units &amp; Standards</text>
        <text x={802} y={400} textAnchor="middle" fontSize="8.5" fill="#a04010">µm · 1 µm = 0.001 mm = 0.00004 in</text>
        <text x={802} y={414} textAnchor="middle" fontSize="8.5" fill="#a04010">ISO 4287/4288 · ASME B46.1 (US)</text>
        <text x={802} y={428} textAnchor="middle" fontSize="8" fill="#7a4a28">Ra (µm) = Ra (µin) × 0.0254</text>
      </svg>
      <div style={S.dCaption}>
        The ISO 1302 symbol encodes up to five requirements in a compact callout. The letter positions (a–e) are standardised — every mechanical engineer must be able to read them quickly.
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Ra to N-grade conversion + comparison bar
// ─────────────────────────────────────────────────────────────────────
function DiagramRaScale() {
  const W = 960, H = 300;

  // ISO N-grades: N1–N12
  const grades = [
    { n:'N1',  ra:0.025, app:'Optical surfaces, gauge faces' },
    { n:'N2',  ra:0.05,  app:'Gauge blocks, precision bores' },
    { n:'N3',  ra:0.1,   app:'Fine grinding, lapping' },
    { n:'N4',  ra:0.2,   app:'Precision honing, fine boring' },
    { n:'N5',  ra:0.4,   app:'Fine turning, grinding' },
    { n:'N6',  ra:0.8,   app:'General machining, shaft fits' },
    { n:'N7',  ra:1.6,   app:'Milling, turning, reaming' },
    { n:'N8',  ra:3.2,   app:'Coarse turning, drilling' },
    { n:'N9',  ra:6.3,   app:'Rough machining' },
    { n:'N10', ra:12.5,  app:'Rough casting/forging finish' },
    { n:'N11', ra:25,    app:'Sand casting' },
    { n:'N12', ra:50,    app:'As-rolled, unfinished' },
  ];

  const barMaxRa = 50;
  const startX = 30, endX = 750;
  const barAreaW = endX - startX;
  const rowH = (H - 60) / grades.length;

  // Log scale mapping
  const logMin = Math.log10(0.02), logMax = Math.log10(55);
  function xPos(ra) {
    return startX + ((Math.log10(ra) - logMin) / (logMax - logMin)) * barAreaW;
  }

  const gradeCols = grades.map((_,i) => {
    const t = i / (grades.length - 1);
    // interpolate from light to dark orange-brown
    const r = Math.round(196 + (122-196)*t);
    const g = Math.round(90 + (46-90)*t);
    const b = Math.round(16 + (0-16)*t);
    return `rgb(${r},${g},${b})`;
  });

  const ticks = [0.025, 0.05, 0.1, 0.2, 0.4, 0.8, 1.6, 3.2, 6.3, 12.5, 25, 50];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>ISO N-Grade Scale — Ra Values, Roughness Numbers &amp; Typical Applications</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="arNS" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
        </defs>

        {/* Axis ticks and labels */}
        {ticks.map((v,i) => {
          const x = xPos(v);
          return (
            <g key={i}>
              <line x1={x} y1={26} x2={x} y2={H-20} stroke="#e8c89a" strokeWidth="0.8" strokeDasharray="3,3"/>
              <text x={x} y={20} textAnchor="middle" fontSize="7.5" fill="#7a4a28">{v}</text>
            </g>
          );
        })}
        <text x={startX + barAreaW/2} y={10} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#7a2e00">
          Ra (µm) — log scale · finer ← → coarser
        </text>

        {/* Bars */}
        {grades.map((g, i) => {
          const y = 28 + i * rowH;
          const barW = xPos(g.ra) - startX;
          return (
            <g key={i}>
              <text x={startX - 4} y={y + rowH*0.65} textAnchor="end"
                fontSize="9.5" fontWeight="700" fill="#7a2e00">{g.n}</text>
              <rect x={startX} y={y + rowH*0.1} width={barW} height={rowH*0.8}
                fill={gradeCols[i]} rx={2} opacity="0.82"/>
              <text x={xPos(g.ra) + 5} y={y + rowH*0.65}
                fontSize="9" fontFamily="'Courier New',monospace" fontWeight="700" fill="#7a2e00">
                {g.ra} µm
              </text>
              <text x={xPos(g.ra) + 60} y={y + rowH*0.65}
                fontSize="8" fill="#7a4a28">{g.app}</text>
            </g>
          );
        })}

        {/* "Finer / Coarser" arrow */}
        <line x1={startX+10} y1={H-14} x2={endX-10} y2={H-14}
          stroke="#c45a10" strokeWidth="1.5" markerEnd="url(#arNS)"/>
        <text x={startX + 10} y={H - 4} fontSize="8.5" fontWeight="700" fill="#c45a10">← Finer (precision)</text>
        <text x={endX - 10} y={H - 4} textAnchor="end" fontSize="8.5" fontWeight="700" fill="#c45a10">Coarser (rough) →</text>
      </svg>
      <div style={S.dCaption}>
        Each N-grade represents a factor of 2 step in Ra. The scale spans four orders of magnitude — from polished gauge faces at Ra 0.025 µm to raw castings at Ra 50 µm.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Lay direction symbols
// ─────────────────────────────────────────────────────────────────────
function DiagramLayDirections() {
  const W = 960, H = 260;
  const dirs = [
    { sym:'=',  label:'Parallel',   desc:'Lay parallel to surface projection\nTypical: plain turning',
      draw: (cx,cy,s) => <g>{[-20,-10,0,10,20].map(dy=><line key={dy} x1={cx-s} y1={cy+dy} x2={cx+s} y2={cy+dy} stroke="#c45a10" strokeWidth="1.5" opacity="0.7"/>)}</g> },
    { sym:'⊥',  label:'Perp.',      desc:'Lay perpendicular to projection\nTypical: facing cuts',
      draw: (cx,cy,s) => <g>{[-20,-10,0,10,20].map(dx=><line key={dx} x1={cx+dx} y1={cy-s} x2={cx+dx} y2={cy+s} stroke="#c45a10" strokeWidth="1.5" opacity="0.7"/>)}</g> },
    { sym:'X',  label:'Crossed',    desc:'Lay crossing in two directions\nTypical: cross-hatch honing',
      draw: (cx,cy,s) => <g>
        {[-16,-6,4,14].map(d=><line key={`a${d}`} x1={cx-s} y1={cy+d} x2={cx+s} y2={cy+d+14} stroke="#c45a10" strokeWidth="1.2" opacity="0.6"/>)}
        {[-16,-6,4,14].map(d=><line key={`b${d}`} x1={cx-s} y1={cy+d+14} x2={cx+s} y2={cy+d} stroke="#c45a10" strokeWidth="1.2" opacity="0.6"/>)}
      </g> },
    { sym:'M',  label:'Multi-dir.', desc:'Lay multidirectional\nTypical: grinding, lapping',
      draw: (cx,cy,s) => <g>
        {[0,45,90,135,180,225,270,315].map((a,i)=>{
          const r=a*Math.PI/180;
          return <line key={i} x1={cx} y1={cy} x2={cx+s*0.7*Math.cos(r)} y2={cy+s*0.7*Math.sin(r)} stroke="#c45a10" strokeWidth="1.5" opacity="0.65"/>;
        })}
      </g> },
    { sym:'C',  label:'Circular',   desc:'Lay approximately circular\nTypical: face turning on lathe',
      draw: (cx,cy,s) => <g>
        {[18,26,34].map(r=><circle key={r} cx={cx} cy={cy} r={r} fill="none" stroke="#c45a10" strokeWidth="1.5" opacity="0.6"/>)}
      </g> },
    { sym:'R',  label:'Radial',     desc:'Lay approximately radial\nTypical: end-facing',
      draw: (cx,cy,s) => <g>
        {[0,30,60,90,120,150].map((a,i)=>{
          const r=a*Math.PI/180;
          return <line key={i} x1={cx+10*Math.cos(r)} y1={cy+10*Math.sin(r)} x2={cx+s*Math.cos(r)} y2={cy+s*Math.sin(r)} stroke="#c45a10" strokeWidth="1.5" opacity="0.65"/>;
        })}
      </g> },
    { sym:'P',  label:'Particulate', desc:'Non-directional, particulate\nTypical: EDM, shot peening',
      draw: (cx,cy,s) => {
        // Deterministic dot positions (pre-computed pseudo-random offsets)
        const dots=[[0.12,0.1],[0.7,0.3],[0.35,0.6],[0.85,0.05],[0.5,0.8],
          [0.2,0.45],[0.9,0.55],[0.6,0.15],[0.05,0.75],[0.75,0.7],
          [0.4,0.25],[0.15,0.9],[0.55,0.5],[0.3,0.85],[0.8,0.4],
          [0.65,0.95],[0.1,0.35],[0.45,0.05],[0.95,0.2],[0.25,0.6],
          [0.72,0.78],[0.38,0.42],[0.88,0.88],[0.02,0.18]];
        return <g>{dots.map(([fx,fy],i)=>(
          <circle key={i} cx={cx-s+fx*2*s} cy={cy-s*0.65+fy*s*1.3} r={1.8} fill="#c45a10" opacity="0.65"/>
        ))}</g>;
      } },
  ];

  const colW = W / dirs.length;
  const boxS = 40; // half-size of texture preview box

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Lay Direction Symbols — ISO 1302 Texture Orientation</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        {dirs.map((d, i) => {
          const cx = colW * i + colW / 2;
          const cy = 120;
          return (
            <g key={i}>
              {/* Symbol */}
              <text x={cx} y={30} textAnchor="middle" fontSize="22" fontWeight="700" fontFamily="'Courier New',monospace" fill="#7a2e00">
                {d.sym}
              </text>
              <text x={cx} y={45} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#a04010">{d.label}</text>

              {/* Texture preview box */}
              <rect x={cx-boxS} y={cy-boxS*0.65} width={boxS*2} height={boxS*1.3}
                fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5" rx={4}/>
              {/* Clip texture lines inside box */}
              <clipPath id={`clip${i}`}>
                <rect x={cx-boxS+2} y={cy-boxS*0.65+2} width={boxS*2-4} height={boxS*1.3-4}/>
              </clipPath>
              <g clipPath={`url(#clip${i})`}>
                {d.draw(cx, cy, boxS)}
              </g>

              {/* Description lines */}
              {d.desc.split('\n').map((line, li) => (
                <text key={li} x={cx} y={cy + boxS*0.7 + 22 + li*15}
                  textAnchor="middle" fontSize="8.5" fill="#7a4a28">{line}</text>
              ))}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        The lay direction affects friction, fluid retention, sealing, and fatigue. Honed cylinder bores use crossed (X) lay to retain oil; sealing surfaces often need circular (C) or multidirectional (M).
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 6 — Functional effects: effect matrix heatmap
// Roughness vs functional property
// ─────────────────────────────────────────────────────────────────────
function DiagramFunctionalEffects() {
  const W = 960, H = 320;

  const props = ['Sliding friction','Wear rate','Sealing ability','Fatigue strength','Corrosion resist.','Press-fit strength','Paint adhesion','Thermal contact','Oil film retention'];
  const levels = ['N1–N3\n(Ra<0.1µm)','N4–N6\n(Ra 0.2–0.8)','N7–N8\n(Ra 1.6–3.2)','N9–N10\n(Ra 6–12)','N11–N12\n(Ra 25–50)'];

  // Effect: 0=bad, 1=poor, 2=ok, 3=good, 4=best
  const data = [
    // friction:        N1-3  N4-6  N7-8  N9-10  N11-12
    [3, 4, 3, 2, 1],
    // wear:
    [4, 4, 3, 2, 1],
    // sealing:
    [4, 4, 2, 1, 0],
    // fatigue:
    [4, 4, 3, 2, 1],
    // corrosion:
    [3, 4, 3, 2, 1],
    // press-fit:
    [2, 4, 3, 2, 1],
    // paint adhesion:
    [1, 2, 3, 4, 3],
    // thermal contact:
    [4, 4, 2, 1, 0],
    // oil retention:
    [0, 1, 3, 4, 4],
  ];

  const colors = ['#c0392b','#d4874a','#e8b07a','#7a9e6a','#2a7a3b'];
  const labels = ['Poor','Fair','OK','Good','Best'];

  const cellW = (W - 220) / levels.length;
  const cellH = (H - 70) / props.length;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Functional Effects of Surface Roughness — Effect Matrix by Application Property</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Column headers */}
        {levels.map((lv, j) => {
          const cx = 220 + j * cellW + cellW / 2;
          const lines = lv.split('\n');
          return (
            <g key={j}>
              <rect x={220 + j * cellW} y={2} width={cellW - 2} height={42} fill="#fdf0e4" stroke="#e8b07a" strokeWidth="1" rx={3}/>
              {lines.map((line, li) => (
                <text key={li} x={cx} y={14 + li * 14} textAnchor="middle"
                  fontSize="8.5" fontWeight={li===0?'700':'400'} fill="#7a2e00">{line}</text>
              ))}
            </g>
          );
        })}

        {/* Rows */}
        {props.map((prop, i) => {
          const cy = 48 + i * cellH;
          return (
            <g key={i}>
              {/* Row label */}
              <rect x={0} y={cy} width={218} height={cellH - 2} fill={i%2===0?'#fdf4ec':'#fdf6ee'} stroke="#e8c89a" strokeWidth="0.8"/>
              <text x={210} y={cy + cellH/2 + 4} textAnchor="end" fontSize="9" fontWeight="700" fill="#7a2e00">{prop}</text>

              {/* Cells */}
              {data[i].map((score, j) => {
                const cx = 220 + j * cellW + cellW / 2;
                return (
                  <g key={j}>
                    <rect x={220 + j * cellW} y={cy} width={cellW - 2} height={cellH - 2}
                      fill={colors[score]} rx={3} opacity="0.72"/>
                    <text x={cx} y={cy + cellH/2 + 4} textAnchor="middle"
                      fontSize="8.5" fontWeight="700" fill="#fff">{labels[score]}</text>
                  </g>
                );
              })}
            </g>
          );
        })}

        {/* Legend */}
        {colors.map((c, i) => (
          <g key={i}>
            <rect x={220 + i * 96} y={H - 22} width={14} height={12} fill={c} rx={2} opacity="0.8"/>
            <text x={238 + i * 96} y={H - 12} fontSize="8.5" fill="#7a2e00">{labels[i]}</text>
          </g>
        ))}
        <text x={220 + 5 * 96 + 20} y={H - 12} fontSize="8.5" fontStyle="italic" fill="#7a4a28">← for the given functional property</text>
      </svg>
      <div style={S.dCaption}>
        No single roughness is universally "best" — oil retention actually benefits from moderate roughness (N9), while sealing surfaces require the finest possible finish (N4–N5).
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 7 — Measurement methods comparison
// Stylised cross-sections of contact stylus vs optical methods
// ─────────────────────────────────────────────────────────────────────
function DiagramMeasurement() {
  // H=400: extra room for bottom labels and table rows without clipping
  const W = 960, H = 400;

  // Surface profile — shifted down to y=240 so stylus arm/label has clear space above
  const surfX = Array.from({length:60},(_,i)=>i*4);
  const surfY = [0,3,-2,4,-1,3,0,-3,4,-2,3,-1,0,4,-3,2,-1,3,0,-2,4,-1,3,-3,1,0,4,-2,3,-1,0,3,-2,4,-1,2,0,-3,3,-2,4,-1,3,0,-2,4,-1,3,-3,1,0,3,-2,4,-1,2,0,-3,3,-2,4];

  const profilePts = surfX.map((x, i) => `${60 + x},${240 + surfY[i]}`).join(' ');

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Surface Roughness Measurement Methods — Stylus Profilometer vs Optical Techniques</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="arME" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
          <pattern id="surfHatch2" width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="5" stroke="#d4874a" strokeWidth="1.2" opacity="0.5"/>
          </pattern>
        </defs>

        {/* ─── PANEL 1: Contact Stylus ─── */}
        <rect x={10} y={10} width={290} height={H - 20} rx={6} fill="none" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={155} y={27} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Contact Stylus Profilometer</text>

        {/* Surface material fill — base at H-10 */}
        <polygon points={`60,${H-10} ${profilePts} 300,${H-10}`} fill="url(#surfHatch2)"/>
        <polyline points={profilePts} fill="none" stroke="#7a2e00" strokeWidth="2"/>

        {/* Stylus components — centred at x=148, tip reaches surface at y=240 */}
        {/* Transducer box */}
        <rect x={70} y={88} width={70} height={30} rx={4} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={105} y={106} textAnchor="middle" fontSize="8" fill="#7a2e00">Transducer</text>
        <text x={105} y={116} textAnchor="middle" fontSize="7.5" fill="#a04010">LVDT/piezo</text>
        {/* Stylus arm */}
        <rect x={100} y={118} width={96} height={9} rx={3} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
        {/* Stylus body (needle) */}
        <polygon points="148,127 144,194 148,198 152,194" fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5"/>
        {/* Diamond tip */}
        <polygon points="148,198 144,210 148,215 152,210" fill="#2a6496"/>
        {/* Scan direction arrow */}
        <line x1={110} y1={180} x2={220} y2={180} stroke="#c45a10" strokeWidth="1.5" strokeDasharray="4,3" markerEnd="url(#arME)"/>
        <text x={165} y={195} textAnchor="middle" fontSize="8" fill="#c45a10">scan direction</text>

        {/* Labels — start well below surface at y=255 */}
        {[
          [18, 258, 'Tip radius: 2–5 µm (diamond)'],
          [18, 272, 'Stylus force: 0.4–4 mN'],
          [18, 286, 'Speed: 0.1–1 mm/s'],
          [18, 300, 'Range: Ra 0.02–100 µm'],
          [18, 314, 'Resolution: < 0.001 µm vertical'],
          [18, 328, 'Scan length: up to 100 mm'],
          [18, 346, '⚠ Can scratch soft surfaces'],
          [18, 360, '⚠ Can miss narrow valleys'],
        ].map(([x, y, text], i) => (
          <text key={i} x={x} y={y} fontSize="8.5" fill={text.startsWith('⚠')?'#c0392b':'#7a4a28'}>{text}</text>
        ))}

        {/* ─── PANEL 2: White Light Interferometry ─── */}
        <rect x={315} y={10} width={310} height={H - 20} rx={6} fill="none" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={470} y={27} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">White Light Interferometry (WLI)</text>

        {/* Optics schematic */}
        <circle cx={470} cy={55} r={13} fill="#e8b07a" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={470} y={59} textAnchor="middle" fontSize="8" fontWeight="700" fill="#7a2e00">LED</text>
        <line x1={470} y1={68} x2={470} y2={108} stroke="#c45a10" strokeWidth="1.5" strokeDasharray="3,2"/>
        {/* Beam splitter */}
        <rect x={448} y={106} width={44} height={10} rx={2} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
        <text x={470} y={126} textAnchor="middle" fontSize="7.5" fill="#7a2e00">Beam splitter</text>
        {/* Reference arm left */}
        <line x1={448} y1={111} x2={406} y2={111} stroke="#c45a10" strokeWidth="1.5" strokeDasharray="3,2"/>
        <rect x={385} y={100} width={22} height={22} rx={2} fill="#2a6496" opacity="0.5" stroke="#2a6496" strokeWidth="1.5"/>
        <text x={385} y={134} fontSize="7.5" fill="#2a6496">Ref mirror</text>
        {/* CCD arm right */}
        <line x1={492} y1={111} x2={564} y2={111} stroke="#c45a10" strokeWidth="1.5" strokeDasharray="3,2"/>
        <rect x={562} y={100} width={28} height={22} rx={2} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={576} y={115} textAnchor="middle" fontSize="7.5" fill="#7a2e00">CCD</text>
        {/* Down beam to surface */}
        <line x1={470} y1={116} x2={470} y2={190} stroke="#a04010" strokeWidth="1.5" strokeDasharray="3,2"/>

        {/* Interference fringes */}
        {[0,5,10,15,20].map((dy,i)=>(
          <line key={i} x1={380} y1={196+dy} x2={562} y2={196+dy}
            stroke="#c45a10" strokeWidth="1.2" opacity={0.28+i*0.12} strokeDasharray={`${6-i},${2+i}`}/>
        ))}
        <text x={470} y={192} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#a04010">interference fringes → height map</text>

        {/* WLI Surface */}
        {(() => {
          const pts2 = surfX.map((x,i)=>`${315+45+x*3.4},${240+surfY[i]}`).join(' ');
          return (
            <g>
              <polygon points={`360,${H-10} ${pts2} 625,${H-10}`} fill="url(#surfHatch2)"/>
              <polyline points={pts2} fill="none" stroke="#7a2e00" strokeWidth="2"/>
            </g>
          );
        })()}

        {/* WLI Labels */}
        {[
          [323, 258, 'Non-contact — no surface damage'],
          [323, 272, 'Full field: millions of points at once'],
          [323, 286, 'Lateral res.: 0.5–5 µm (objective)'],
          [323, 300, 'Vertical res.: < 1 nm'],
          [323, 314, 'Speed: full area in seconds'],
          [323, 332, '⚠ Limited by numerical aperture'],
          [323, 346, '⚠ Can fail on steep slopes / shiny'],
        ].map(([x, y, text], i) => (
          <text key={i} x={x} y={y} fontSize="8.5" fill={text.startsWith('⚠')?'#c0392b':'#7a4a28'}>{text}</text>
        ))}

        {/* ─── PANEL 3: Method comparison table ─── */}
        <rect x={638} y={10} width={312} height={H - 20} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={794} y={27} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Method Comparison</text>
        <line x1={646} y1={33} x2={942} y2={33} stroke="#e8b07a"/>
        {[
          ['Method',           'Vert.res.','Speed',   'Contact'],
          ['Stylus (skid)',     '0.01 µm', 'Slow',    'Yes'],
          ['Stylus (datum)',    '1 nm',    'Slow',    'Yes'],
          ['WLI / PSI',        '< 1 nm',  'Fast',    'No'],
          ['Confocal (laser)', '5 nm',    'Medium',  'No'],
          ['Focus variation',  '10 nm',   'Fast',    'No'],
          ['AFM',              '0.01 nm', 'V.slow',  'Yes'],
          ['SEM + EDS',        'N/A',     'Slow',    'N/A'],
          ['Tactile CMM',      '0.5 µm',  'Slow',    'Yes'],
        ].map(([a,b,c,d], i) => {
          const y = 40 + i * 36;
          const isHead = i === 0;
          return (
            <g key={i}>
              <rect x={646} y={y} width={296} height={34}
                fill={isHead?'#a04010':i%2===0?'#fdf4ec':'#fdf6ee'} rx={isHead?3:0}/>
              <text x={652} y={y+21} fontSize={isHead?'9':'8.5'} fontWeight={isHead?'700':'400'} fill={isHead?'#fff':'#7a2e00'}>{a}</text>
              <text x={775} y={y+21} fontSize={isHead?'9':'8.5'} fontWeight={isHead?'700':'400'} fill={isHead?'#fff':'#a04010'} fontFamily="'Courier New',monospace">{b}</text>
              <text x={846} y={y+21} fontSize={isHead?'9':'8.5'} fontWeight={isHead?'700':'400'} fill={isHead?'#fff':'#a04010'} fontFamily="'Courier New',monospace">{c}</text>
              <text x={897} y={y+21} fontSize={isHead?'9':'8.5'} fontWeight={isHead?'700':'400'} fill={isHead?'#fff':d==='Yes'?'#c0392b':'#2a7a3b'} fontFamily="'Courier New',monospace">{d}</text>
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        Stylus profilometers remain the ISO-preferred contact method for most workshop applications. Optical methods (WLI, confocal) are faster and non-destructive but can struggle with very rough or reflective surfaces.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────────────────────────────
export default function SurfaceRoughness() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Surface Roughness in Mechanical Engineering</h1>
      <p style={S.p}>
        Every machined, cast, or formed surface has a micro-texture — a characteristic pattern of peaks and valleys left by the manufacturing process.
        <strong> Surface roughness</strong> quantifies these irregularities and directly determines how a part performs in friction,
        wear, sealing, fatigue, and assembly. Specifying the wrong finish adds unnecessary cost; specifying too coarse a finish causes premature failure.
      </p>
      <p style={S.p}>
        This reference covers the key roughness parameters, ISO symbology, manufacturing process capabilities, functional effects, measurement methods,
        and selection guidelines used daily in mechanical design.
      </p>

      <h2 style={S.h2}>Surface Profile Anatomy &amp; Parameters</h2>
      <DiagramProfileAnatomy />
      <Table2 rows={[
        ['Ra — Arithmetic mean','Most widely used parameter worldwide · Ra = (1/l)∫|z(x)|dx\nEasy to measure, easy to specify on drawings\nDoes NOT distinguish peaks from valleys — skewness and kurtosis needed for that distinction'],
        ['Rq — RMS roughness','Root mean square of profile deviations · Rq = √(1/l∫z²dx)\nMore sensitive to peaks and valleys than Ra · Rq ≈ 1.11 × Ra for Gaussian surfaces\nPreferred in optics and precision engineering where extreme deviations matter'],
        ['Rz — Ten-point height','ISO: avg height of 5 highest peaks + 5 lowest valleys within evaluation length\nDIN (older): max peak to max valley in sampling length · verify which standard applies\nBetter indicator of extreme events than Ra · often Rz ≈ 4–7 × Ra'],
        ['Rt — Total height','Absolute max peak-to-valley height across entire evaluation length\nSingle worst-case value — important for sealing, coating adhesion, fit clearance\nVery sensitive to single outlier defects (scratches, pits)'],
        ['Rsk — Skewness','Asymmetry of height distribution · Rsk = 0 is Gaussian (symmetric)\nRsk < 0: plateau-like surface (ground, honed) — good for bearing and sealing\nRsk > 0: peaks dominate (turned, milled) — higher wear at initial run-in'],
        ['Rku — Kurtosis','Peakedness of distribution · Rku = 3 for Gaussian\nRku > 3: many sharp asperities — high initial contact stress, higher friction\nRku < 3: broad hills — lower stress concentration, better conformability'],
        ['Rsm — Spacing','Mean spacing of adjacent profile peaks along the mean line\nSmall Rsm → fine texture (many peaks) · Large Rsm → coarse widely spaced features\nInfluences oil film build-up, reflectivity, and friction at sliding interfaces'],
        ['Waviness (Wt, Wa)','Long-wavelength component separated by cut-off filter (λc)\nCaused by machine vibration, tool wear, workpiece deflection\nDistinguished from roughness by: roughness = λs to λc · waviness = λc to λf'],
        ['Filter cut-off λc','ISO 4288 specifies λc based on Ra range: Ra 0.1–2 µm → λc = 0.8 mm\nλc 2.5 mm for Ra 2–10 µm · λc 8 mm for Ra 10–80 µm\nUsing wrong cut-off makes same surface appear to have different Ra — must be stated on drawing'],
      ]}/>

      <h2 style={S.h2}>Manufacturing Process Roughness Ranges</h2>
      <DiagramProcessRanges />
      <Table2 rows={[
        ['Grinding','Produces Ra 0.1–3.2 µm depending on grit, speed, feed · wheel loading degrades finish progressively\nCylindrical grinding finer than surface grinding for same grit · dressing interval critical\nTypical lay: transverse (⊥) to grinding direction · often reversed for cross-grinding'],
        ['Turning & facing','Ra 0.4–12.5 µm depending on tool radius, feed, speed, chip breaking\nFeed marks visible at low magnification · tool nose radius rε and feed f: Rth ≈ f²/(8rε)\nDiamond turning achieves Ra < 0.1 µm on aluminium and copper for mirror surfaces'],
        ['Milling','Ra 0.8–6.3 µm typical · flat bottom end mill leaves cusp pattern proportional to stepover\nBall-end cusp height: h = ae²/(8r) where ae = stepover, r = ball radius\nFace milling with wiper insert can achieve Ra < 0.4 µm on aluminium/cast iron'],
        ['Honing','Ra 0.05–0.8 µm · primary process for cylinder bores — produces cross-hatch lay (X)\nCross-hatch angle 30–60° from bore axis — optimised for oil film retention\nPlateau honing: rough hone then light finish hone removes sharp peaks → Rsk < 0'],
        ['Lapping','Ra 0.01–0.4 µm · used for gauge faces, sealing flanges, lapped joints\nAbrasive grain in oil between flat reference plate and workpiece\nAchieves very flat and parallel surfaces — often combined with super-finishing'],
        ['EDM (sinking)','Ra 0.2–12.5 µm depending on current density · surface is re-cast layer + micro-craters\nFine EDM with low current: Ra < 0.2 µm but slow · rough EDM: Ra > 6 µm, fast removal\nRe-cast layer (white layer) is brittle — may require grinding or etching for fatigue-critical parts'],
        ['Sand casting','Ra 6.3–50 µm · surface dominated by sand grain impressions and solidification shrinkage\nCleanliness, moisture, and sand grain size all affect finish · chills improve local finish\nPost-cast machining usually needed for mating surfaces — specify allowance on drawing'],
        ['Investment casting','Ra 1.6–6.3 µm · much finer than sand casting — ceramic shell from wax pattern\nMany net-shape features possible without machining · tolerances ±0.1–0.3 mm typical\nGood for aerospace and medical components — thin walls and complex undercuts feasible'],
      ]}/>

      <h2 style={S.h2}>ISO 1302 Surface Texture Symbology</h2>
      <DiagramISOSymbol />
      <Table2 rows={[
        ['Symbol placement','Arrow or leader line touches the surface it describes · symbol reads from bottom or right\nDo NOT place on hidden lines · use "all surfaces" symbol on drawing or title block to avoid repeating\nIn 3D CAD: attach to faces directly; GD&T annotations preferred over separate drawing callouts'],
        ['Upper & lower limits','Single value (a only): upper limit — all measured values must be below it\nTwo values (a and b): "U" = upper, "L" = lower — both must be satisfied\nDefault filter: Gaussian (phase-correct) per ISO 16610 — specify if different filter needed'],
        ['16% vs max rule','Default: 16% rule — 84% of all measured values must be within spec (Gaussian assumption)\nMax rule: ALL values within spec — add "max" after number (e.g. Ra 0.8 max)\nMax rule is significantly tighter — harder to achieve — use only when all readings matter'],
        ['Process indication','Basic symbol (no bar): any process acceptable\nWith bar (tick): material removal by machining required\nWith circle: no material removal — supplied condition, moulded, or formed only'],
        ['Older standards','ASME B46.1 uses 60° V symbol with Ra in µin or µm · no ISO position lettering\n"125 µin AA" = Ra 3.2 µm approximately · "32 µin" = Ra 0.8 µm\nConversion: Ra (µm) = Ra (µin) × 0.0254 · modern drawings should specify µm only'],
      ]}/>

      <h2 style={S.h2}>ISO N-Grade Scale</h2>
      <DiagramRaScale />

      <h2 style={S.h2}>Lay Direction</h2>
      <DiagramLayDirections />
      <Table2 rows={[
        ['Parallel (=)','Lay runs parallel to projection plane edge — typical of plain longitudinal turning\nLow friction in direction parallel to lay · higher friction transverse\nNot suitable for sealing surfaces where fluid could track along grooves'],
        ['Perpendicular (⊥)','Lay perpendicular to projection edge — face turning, transverse milling\nBetter sealing than parallel lay · grooves run across potential leak path\nCommon on flanges, valve seats, and cylinder head gasket faces'],
        ['Crossed (X)','Two directions crossing — cross-hatch produced by honing or cross-grinding\nOptimal for bearing surfaces: retains oil in diamond-pattern pockets\nCylinder bore honing: 30–60° angle · plateau hone for running-in performance'],
        ['Circular (C)','Concentric circular pattern — face turning on lathe, face grinding\nUsed on thrust faces, end covers, and rotary sealing surfaces\nFluid seal relies on circular groove interrupting radial leak path'],
        ['Multidirectional (M)','No preferred direction — grinding, lapping, polishing, EDM, shot peening\nIsotropic — friction and wear roughly equal in all directions\nPreferred for mating surfaces assembled in unknown orientation'],
        ['Radial (R)','Radial from centre — end face turning, radial milling\nSuitable for thrust washers and face seals in radial direction\nDistinguish from circular: radial grooves can provide leak paths outward'],
        ['Particulate (P)','Non-directional pitting — EDM, sand blasting, shot peening, chemical etch\nRandom texture with no measurable dominant direction\nShot-peened surfaces get Rsk < 0 (compressive residual stress + plateau texture)'],
      ]}/>

      <h2 style={S.h2}>Functional Effects of Surface Roughness</h2>
      <DiagramFunctionalEffects />
      <Table2 rows={[
        ['Friction & wear','Optimal friction at Ra 0.2–0.8 µm — too smooth = adhesive wear (galling), too rough = abrasive\nAsperity contact → Hertz stress concentration → plastic deformation → wear particles\nRun-in period flattens peaks → friction drops → stabilises at "running roughness"'],
        ['Fatigue life','Surface roughness acts as stress concentration sites → crack initiation from valleys\nFatigue limit decreases with increasing Ra — roughly: Kf = 1 + q(Kt−1) where Kt related to Ra\nGround surfaces (Ra 0.4 µm) have ~20–30% higher fatigue limit than turned (Ra 1.6 µm)'],
        ['Sealing surfaces','Static seals (gaskets, flanges): Ra < 1.6 µm typical · O-ring grooves Ra 0.4–0.8 µm\nDynamic lip seals: counter-surface Ra 0.2–0.5 µm (ground, polished) to avoid lip wear\nHydrostatic seals: lapped flanges Ra < 0.4 µm · metal-to-metal seating Ra < 0.2 µm'],
        ['Press and interference fits','Asperities are flattened on assembly → effective interference reduced by ≈ Rz of both surfaces\nAllow for roughness loss: Δi = 0.8(Rz_bore + Rz_shaft) added to required interference\nSurface texture also affects coefficients of static and kinetic friction in pressed joint'],
        ['Coating & adhesion','Thermal spray, PVD, CVD, painting: need Ra 1.6–6.3 µm for mechanical keying\nExcessive roughness causes shadowing in PVD/CVD → pinholes and thickness variation\nElectroplating: rough substrate telegraphs through to plated surface · polish before chrome'],
        ['Thermal contact conductance','Rough surfaces reduce real contact area → higher thermal resistance across joint\nHTC ∝ 1/Ra — doubling Ra roughly halves conductance at same pressure\nThermal interface materials (greases, pads) fill gaps — less sensitive to Ra above 0.4 µm'],
        ['Corrosion','Rough surfaces have larger surface area and more micro-crevices → faster corrosion initiation\nSalt spray and pitting corrosion preferentially nucleate at valley roots (high stress, low O₂)\nPolished stainless steel (Ra < 0.5 µm) is noticeably more corrosion-resistant than rough'],
        ['Oil & lubricant retention','Moderate roughness (Ra 0.8–3.2 µm) in cylinder bores retains oil in peaks/valleys\nPlateau-honed bore with Rsk < 0: flat plateaux carry load; valleys retain oil — best of both\nToo smooth a bore causes oil starvation → cylinder glazing → oil consumption increase'],
      ]}/>

      <h2 style={S.h2}>Measurement Methods</h2>
      <DiagramMeasurement />
      <Table2 rows={[
        ['Stylus profilometer','ISO 12179 primary standard · diamond stylus tip radius 2–10 µm (affects valley resolution)\nSkid-type: tracks slow waviness with skid → measures only roughness · datum type: no skid\nContact force 0.4–4 mN — may scratch gold, PTFE, or soft elastomers · OK for steel/aluminium'],
        ['White light interferometry','Phase-shifting (PSI) for smooth surfaces (Ra < 0.15 µm) · coherence scanning (CSI) for rougher\nFull-field height map in seconds — 1M+ points simultaneously vs stylus single trace\nSensitive to vibration — use vibration isolation table · cannot measure steep slopes > ~70°'],
        ['Confocal microscopy','Laser or white-light confocal: lateral res. ~ λ/2 ≈ 0.2–1 µm · vertical res. ~5–10 nm\nGood for structured surfaces, undercuts, and surfaces too rough for PSI\nSlow for large areas — usually used for small fields of view, defect inspection'],
        ['Atomic force microscopy (AFM)','Sub-nanometre vertical resolution · used for ultra-smooth optics (Ra < 0.01 µm)\nSlow — typically 10×10 µm to 100×100 µm scan area · months to scan a whole part\nNot a production tool — research and calibration standard verification only'],
        ['Calibration standards','Reference specimens traceable to national standards (PTB, NIST, NPL)\nType A: calibration of vertical magnification · Type B: spatial/frequency response\nPer ISO 12179 and ISO 5436: re-calibrate profilometer every 6–12 months in production'],
        ['Measurement uncertainty','ISO/IEC 17025 compliant labs report expanded uncertainty U (k=2, 95% confidence)\nTypical stylus profilometer: U(Ra) ≈ 3–8% · Optical: U(Ra) ≈ 5–10% depending on surface\nDifferent methods and different tip radii WILL give different Ra — standardise in specification'],
      ]}/>

      <h2 style={S.h2}>Selection Guidelines by Application</h2>
      <Table2 rows={[
        ['Rolling element bearings','Housing bore: Ra 0.4–0.8 µm (H7) · shaft seat: Ra 0.2–0.4 µm (k6 or m6)\nRoller tracks (hardened): Ra 0.05–0.2 µm · ground then superfinished\nLoose fits for easy assembly → tighter spec · interference fits → rough = reduced effective interference'],
        ['Sliding bearings (plain)','Journal (shaft): Ra 0.2–0.8 µm · bearing bore: Ra 0.4–1.6 µm\nHydrodynamic bearings need moderate roughness to retain oil at start-up\nCrossed lay on bearing bore preferred · avoid directional lay parallel to sliding direction'],
        ['Hydraulic/pneumatic cylinders','Piston rod: Ra 0.1–0.4 µm (chromed, ground, superfinished) · protects U-cup seal\nBore: Ra 0.2–0.8 µm (honed, plateau) · Rsk < 0 preferred\nRod surface: no axial scratches — each scratch is a potential leak path across seal'],
        ['Gears (involute)','Root fillet: Ra 0.4–0.8 µm — fatigue-critical zone · sometimes shot-peened\nTooth flank: Ra 0.4–1.6 µm depending on speed and noise requirement\nHigh-speed (> 5 m/s pitch line): Ra < 0.8 µm to reduce noise and EHD film breakdown'],
        ['Sealing flanges (gasket)','Raised face (ANSI): Ra 1.6–3.2 µm (serrated concentric — phonographic finish)\nFlat face: Ra 1.6 µm max · ring-type joint (RTJ): Ra 0.4–0.8 µm machined groove\nSpiral wound gaskets tolerate rougher surfaces (Ra up to 3.2 µm) than solid metal gaskets'],
        ['Optical mirrors & lenses','High-precision: Ra < 0.002 µm (2 nm) · single-point diamond turned or polished\nMid-range (IR optics): Ra 0.01–0.05 µm · diamond turned without polishing\nForm error (shape) measured separately — roughness and form are independent specifications'],
        ['Medical implants','CoCr/Ti femoral heads: Ra 0.01–0.05 µm (mirror polished) · minimises UHMWPE wear particles\nPorous coating on stems: Ra 50–200 µm intentionally rough for bone ingrowth (osseointegration)\nISO 7206 specifies finish requirements · traceability required for regulatory submission'],
        ['Sheet metal (stamped)','Draw quality (DQ): Ra 0.6–1.2 µm for good lubricant retention during stamping\nExposed panels (outer body): Ra 0.6–1.0 µm · higher Ra → orange peel in topcoat paint\nZinc-coated (galvanised): coating adds Ra — bare steel needs finer finish than target coated spec'],
      ]}/>

      <h2 style={S.h2}>Tolerancing, Cost, and Design Rules</h2>
      <Table2 rows={[
        ['Tolerance-roughness relationship','Rule of thumb: Ra (µm) ≤ T/20 where T = dimensional tolerance (µm)\nRa 0.8 µm → tolerance T ≥ 16 µm (IT7 on 50mm shaft = 25 µm ✓)\nOver-specifying roughness beyond the dimensional tolerance is meaningless — and expensive'],
        ['Cost vs roughness','Cost increases exponentially as Ra decreases — each step below Ra 0.8 µm roughly doubles cost\nRa 12.5: turning only · Ra 3.2: fine turning · Ra 0.8: grinding · Ra 0.2: fine grinding\nRa 0.05: lapping/superfinishing · Ra 0.01: polishing — often manual'],
        ['Specifying correctly','Always state: parameter (Ra), value (µm), filter cut-off (λc), and process if required\n"Ra 0.8 / λc 0.8 mm" is complete · bare "▽▽▽" (old triangle system) is not acceptable\nLeast-cost principle: specify the coarsest acceptable finish — never "as fine as possible"'],
        ['Inspection planning','Agree measurement method with supplier before production — profilometer vs optical matters\nMeasure in specified direction, at specified location, with traceable instrument\nAcceptance criteria: 16% rule (default) or max rule — state explicitly on drawing or in contract'],
        ['Surface integrity','Roughness alone does not fully characterise surface — also consider:\nResidual stress (compressive = good for fatigue) · microhardness layer · re-cast/white layer\nBurns (grinding burn) can appear with Ra unchanged — requires Barkhausen noise or nital etch to detect'],
        ['GD&T integration','Surface texture is separate from GD&T form tolerances (flatness, roundness, cylindricity)\nA surface can be within flatness tolerance but fail roughness spec, or vice versa\nBoth must be specified independently — flatness on datum surfaces, Ra on functional surfaces'],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "Theoretical peak height (turning):  Rth = f² / (8·rε)   where f = feed (mm/rev), rε = nose radius (mm)",
        "Ball-end cusp height:  h = ae² / (8·r)   where ae = stepover (mm), r = ball radius (mm)",
        "Roughness–tolerance rule:  Ra (µm) ≤ IT / 20   (IT = dimensional tolerance in µm)",
        "Ra to Rz (approx):  Rz ≈ 4 × Ra to 7 × Ra   depending on surface type",
        "Ra to Rq (RMS):  Rq ≈ 1.11 × Ra   for Gaussian height distribution",
        "Interference fit correction:  Δi_lost ≈ 0.8 × (Rz_bore + Rz_shaft)",
        "Filter cut-offs per ISO 4288:  Ra 0.1–2 µm → λc 0.8 mm · Ra 2–10 µm → λc 2.5 mm · Ra 10–80 µm → λc 8 mm",
        "Conversion µin ↔ µm:  Ra (µm) = Ra (µin) × 0.0254   e.g. 32 µin = 0.8 µm",
        "Evaluation length (default):  ln = 5 × λc   (5 consecutive sampling lengths)",
        "ISO N-grade:  Ra (µm) = 0.025 × 2^(N−1)   e.g. N6: Ra = 0.025 × 2^5 = 0.8 µm",
      ]}/>
    </div>
  );
}